import React, { useEffect, useState } from 'react';

import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  ModalFooter,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

type FormErrors = {
  [key: string]: string | boolean;
  type: 'invalid' | 'required';
};

interface ModalUnitConstructiveAddProps extends ModalRootProps {
  data: {
    budget_id: number;
    parent_stage_id: number | null;
    stage_id?: number;
    title?: string;
    description?: string;
  };
}

const ModalUnitConstructiveAdd: React.FC<ModalUnitConstructiveAddProps> = ({
  onConfirm,
  handleClose,
  data,
  mode,
  ...restProps
}) => {
  const [initialLoad, setInitialLoad] = useState(false);

  const [title, setTitle] = useState<string>(data.title || '');
  const titleRef = React.useRef<HTMLInputElement>(null);

  const [description, setDescription] = useState<string>(
    data.description || '',
  );

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<FormErrors | undefined>();

  useEffect(() => {
    if (!initialLoad) {
      setInitialLoad(true);
    }
  }, [initialLoad]);

  useEffect(() => {
    if (initialLoad && titleRef.current) {
      titleRef.current.focus();
    }
  }, [initialLoad]);

  const handleConfirm = async (): Promise<void> => {
    const formErrors: FormErrors = {} as FormErrors;

    if (!title) {
      Object.assign(formErrors, { title: true, type: 'required' });
    }

    setErrors(formErrors);
    if (Object.keys(formErrors).length) {
      return;
    }

    try {
      setLoading(true);

      if (mode === 'add') {
        await api.post(`/budget/${data.budget_id}/component`, {
          parent_stage_id: data.parent_stage_id,
          components: [
            {
              type: 'unit_constructive',
              title,
              description: description || title,
              quantity: 1,
            },
          ],
        });
      } else if (mode === 'edit') {
        await api.put(`/budget/${data.budget_id}/component/${data.stage_id}`, {
          type: 'unit_constructive',
          component: {
            title,
            quantity: 1,
            description: description || title,
          },
        });
      }

      if (onConfirm) onConfirm();

      toast({
        description:
          mode === 'add'
            ? 'Unidade construtiva adicionado com sucesso!'
            : 'Unidade construtiva editado com sucesso!',
        status: 'success',
      });

      handleClose();
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          (mode === 'add'
            ? 'Houve um erro ao adicionar o unidade construtiva!'
            : 'Houve um erro ao editar o unidade construtiva!'),
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal size="6xl" scrollBehavior="inside" {...restProps}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {mode === 'add'
            ? 'Adicionar unidade construtiva'
            : 'Editar unidade construtiva'}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          <FormControl isInvalid={!!errors?.title}>
            <FormLabel>Título</FormLabel>

            <Input
              ref={titleRef}
              w="100%"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
            />

            <FormErrorMessage>
              {!!errors?.title && 'Título é obrigatório'}
            </FormErrorMessage>
          </FormControl>

          <FormControl marginTop="4" isInvalid={!!errors?.description}>
            <FormLabel>Descrição</FormLabel>

            <Textarea
              w="100%"
              resize="none"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleConfirm()}
            />
          </FormControl>

          <FormErrorMessage>
            {!!errors?.description && 'Descrição é obrigatória'}
          </FormErrorMessage>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            isDisabled={loading}
            colorScheme="green"
            onClick={handleConfirm}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalUnitConstructiveAdd;
